import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface DetectionCoordinates {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

interface ImageWithDetectionBoxProps {
  src: string;
  alt: string;
  coordinates: DetectionCoordinates;
  onError?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  onLoad?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  className?: string;
  style?: React.CSSProperties;
  showDetectionBox?: boolean;
  showEmployeeName?: boolean;
  employeeName?: string;
  detectionStyle?: 'box' | 'arrow' | 'subtle' | 'none';
  detectionColor?: string;
}

const ImageWithDetectionBox: React.FC<ImageWithDetectionBoxProps> = ({
  src,
  alt,
  coordinates,
  onError,
  onLoad,
  className = '',
  style = {},
  showDetectionBox = true,
  showEmployeeName = true,
  employeeName,
  detectionStyle = 'box',
  detectionColor = 'var(--an-success)'
}) => {
  const { t } = useTranslation('components', { keyPrefix: 'imageWithDetectionBox' });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  useLayoutEffect(() => {
    if (imageRef.current && imageLoaded) {
      setImageDimensions({
        width: imageRef.current.naturalWidth,
        height: imageRef.current.naturalHeight
      });
    }
  }, [imageLoaded]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setImageLoaded(true);
    onLoad?.(e);
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setImageError(true);
    onError?.(e);
  };

  if (imageError) {
    return (
      <div className={`d-flex align-items-center justify-content-center text-muted an-detection-image-frame ${className}`} style={style}>
        <div className="text-center">
          <i className="bi bi-image mb-2" style={{ fontSize: '2rem' }}></i>
          <br />
          <small>{t('imageNotAvailable')}</small>
        </div>
      </div>
    );
  }

  const renderDetectionVisualization = () => {
    if (!showDetectionBox || !imageLoaded || !coordinates || !imageDimensions.width || !imageDimensions.height) return null;

    switch (detectionStyle) {
      case 'box':
        const isPercentage = coordinates.x1 <= 100 && coordinates.y1 <= 100 && coordinates.x2 <= 100 && coordinates.y2 <= 100;
        
        if (isPercentage) {
          const width = coordinates.x2 - coordinates.x1;
          const height = coordinates.y2 - coordinates.y1;
          
          return (
            <div
              className="position-absolute an-detection-overlay-box"
              style={{
                top: `${coordinates.y1}%`,
                left: `${coordinates.x1}%`,
                width: `${width}%`,
                height: `${height}%`,
                border: `2px solid var(--an-success)`,
                pointerEvents: 'none',
                zIndex: 10
              }}
            />
          );
        } else {
          const x1Percent = (coordinates.x1 / imageDimensions.width) * 100;
          const y1Percent = (coordinates.y1 / imageDimensions.height) * 100;
          const x2Percent = (coordinates.x2 / imageDimensions.width) * 100;
          
          const width = x2Percent - x1Percent;
          const height = coordinates.y2 - coordinates.y1;
          
          return (
            <div
              className="position-absolute an-detection-overlay-box"
              style={{
                top: `${y1Percent}%`,
                left: `${x1Percent}%`,
                width: `${width}%`,
                height: `${height}px`,
                border: `2px solid var(--an-success)`,
                pointerEvents: 'none',
                zIndex: 10
              }}
            />
          );
        }

      case 'arrow':
        const isPercentageArrow = coordinates.x1 <= 100 && coordinates.y1 <= 100 && coordinates.x2 <= 100 && coordinates.y2 <= 100;
        
        let faceCenterX, faceTopY;
        
        if (isPercentageArrow) {
          faceCenterX = (coordinates.x1 + coordinates.x2) / 2;
          faceTopY = coordinates.y1;
          
          const startX = (faceCenterX / 100) * imageDimensions.width;
          const startY = (faceTopY / 100) * imageDimensions.height;
          const endX = startX;
          const endY = Math.max(0, startY - 40);
          
          return (
            <svg
              className="position-absolute an-detection-overlay-box"
              style={{
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 10
              }}
              viewBox={`0 0 ${imageDimensions.width} ${imageDimensions.height}`}
              preserveAspectRatio="none"
            >
              <defs>
                <marker
                  id={`arrowhead-${Math.random()}`}
                  markerWidth="10"
                  markerHeight="7"
                  refX="9"
                  refY="3.5"
                  orient="auto"
                >
                  <polygon
                    points="0 0, 10 3.5, 0 7"
                    fill={detectionColor}
                    stroke={detectionColor}
                    strokeWidth="1"
                  />
                </marker>
              </defs>
              
              <line
                x1={endX}
                y1={endY}
                x2={startX}
                y2={startY}
                stroke={detectionColor}
                strokeWidth="1.5"
                markerEnd={`url(#arrowhead-${Math.random()})`}
                opacity="0.9"
              />
            </svg>
          );
        } else {
          faceCenterX = (coordinates.x1 + coordinates.x2) / 2;
          faceTopY = coordinates.y1;
          
          const endX = faceCenterX;
          const endY = Math.max(0, faceTopY - 40);
          
          return (
            <svg
              className="position-absolute an-detection-overlay-box"
              style={{
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 10
              }}
              viewBox={`0 0 ${imageDimensions.width} ${imageDimensions.height}`}
              preserveAspectRatio="none"
            >
              <defs>
                <marker
                  id={`arrowhead-${Math.random()}`}
                  markerWidth="10"
                  markerHeight="7"
                  refX="9"
                  refY="3.5"
                  orient="auto"
                >
                  <polygon
                    points="0 0, 10 3.5, 0 7"
                    fill={detectionColor}
                    stroke={detectionColor}
                    strokeWidth="1"
                  />
                </marker>
              </defs>
              
              <line
                x1={endX}
                y1={endY}
                x2={faceCenterX}
                y2={faceTopY}
                stroke={detectionColor}
                strokeWidth="1.5"
                markerEnd={`url(#arrowhead-${Math.random()})`}
                opacity="0.9"
              />
            </svg>
          );
        }

      case 'subtle':
        const isPercentageSubtle = coordinates.x1 <= 100 && coordinates.y1 <= 100 && coordinates.x2 <= 100 && coordinates.y2 <= 100;
        
        if (isPercentageSubtle) {
          const width = coordinates.x2 - coordinates.x1;
          const height = coordinates.y2 - coordinates.y1;
          
          return (
            <div
              className="position-absolute an-detection-overlay-box"
              style={{
                top: `${coordinates.y1}%`,
                left: `${coordinates.x1}%`,
                width: `${width}%`,
                height: `${height}%`,
                border: `1px solid ${detectionColor}`,
                opacity: 0.3,
                pointerEvents: 'none',
                zIndex: 5
              }}
            />
          );
        } else {
          const x1Percent = (coordinates.x1 / imageDimensions.width) * 100;
          const y1Percent = (coordinates.y1 / imageDimensions.height) * 100;
          const x2Percent = (coordinates.x2 / imageDimensions.width) * 100;
          
          const width = x2Percent - x1Percent;
          const height = coordinates.y2 - coordinates.y1;
          
          return (
            <div
              className="position-absolute an-detection-overlay-box"
              style={{
                top: `${y1Percent}%`,
                left: `${x1Percent}%`,
                width: `${width}%`,
                height: `${height}px`,
                border: `1px solid ${detectionColor}`,
                opacity: 0.3,
                pointerEvents: 'none',
                zIndex: 5
              }}
            />
          );
        }

      case 'none':
      default:
        return null;
    }
  };

  const getEmployeeNamePosition = () => {
    if (!imageLoaded || !coordinates || !imageDimensions.width || !imageDimensions.height) return {};
    
    const isPercentage = coordinates.x1 <= 100 && coordinates.y1 <= 100 && coordinates.x2 <= 100 && coordinates.y2 <= 100;
    
    const getResponsiveSpacing = () => {
      if (windowWidth < 576) return 15;
      if (windowWidth < 768) return 12;
      if (windowWidth < 992) return 10;
      return 8;
    };
    
    const spacing = getResponsiveSpacing();
    
    if (isPercentage) {
      const centerX = (coordinates.x1 + coordinates.x2) / 2;
      const topY = Math.max(0, coordinates.y1 - spacing);
      
      return {
        left: `${centerX}%`,
        top: `${topY}%`,
        transform: 'translateX(-50%)'
      };
    } else {
      const x1Percent = (coordinates.x1 / imageDimensions.width) * 100;
      const y1Percent = (coordinates.y1 / imageDimensions.height) * 100;
      const x2Percent = (coordinates.x2 / imageDimensions.width) * 100;
      
      const centerX = (x1Percent + x2Percent) / 2;
      const topY = Math.max(0, y1Percent - spacing);
      
      return {
        left: `${centerX}%`,
        top: `${topY}%`,
        transform: 'translateX(-50%)'
      };
    }
  };

  return (
    <div className="position-relative an-detection-image-frame" style={{ display: 'inline-block' }}>
      <img
        ref={imageRef}
        src={src}
        alt={alt}
        className={className}
        style={style}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />
      
      {showEmployeeName && employeeName && imageLoaded && (
        <div
          className="position-absolute employee-name-tag"
          style={{
            ...getEmployeeNamePosition(),
            backgroundColor: 'var(--an-success)',
            color: 'var(--an-white)',
            padding: '2px 8px',
            borderRadius: 'var(--an-radius-sm)',
            fontSize: '0.75rem',
            fontWeight: '500',
            whiteSpace: 'nowrap',
            zIndex: 15,
            boxShadow: 'var(--an-shadow-sm)'
          }}
        >
          <span style={{ marginRight: 6 }}><i className="bi bi-person-fill"></i></span>{employeeName}
          
          <div
            className="position-absolute"
            style={{
              bottom: '-6px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '4px solid transparent',
              borderRight: '4px solid transparent',
              borderTop: '6px solid var(--an-success)'
            }}
          />
        </div>
      )}

      {renderDetectionVisualization()}
    </div>
  );
};

export default ImageWithDetectionBox;
